import React, { useState, useRef, useEffect } from 'react';
import { useUser, useClerk } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Star } from 'lucide-react';
import { useSupporter } from '../../hooks/useSupporter';

const UserProfile = () => {
  const { user } = useUser();
  const { signOut, openSignIn } = useClerk();
  const { t } = useTranslation();
  const supporterData = useSupporter();
  const { isSupporter, supporterLevel, getSupporterDuration } = supporterData;
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Handle clicks outside the dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && isProfileOpen) {
        setIsProfileOpen(false);
      }
    }

    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);
    
    // Clean up
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileOpen]);

  const handleSignOut = async () => {
    await signOut();
  };

  const handleSignIn = () => {
    openSignIn();
  };


  // Show sign-in button if not logged in
  if (!user) {
    return (
      <button
        onClick={handleSignIn}
        className="sign-in-button"
      >
        {t('buttons.signIn')}
      </button>
    );
  }

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setIsProfileOpen(!isProfileOpen)}
        className="user-profile-button"
      >
        <img
          className="user-profile-avatar"
          src={user.imageUrl}
          alt={user.fullName || user.primaryEmailAddress?.emailAddress}
        />
        <span style={{ display: window.innerWidth > 768 ? 'inline' : 'none' }}>
          {user.fullName || user.primaryEmailAddress?.emailAddress}
        </span>
        <svg
          className={`user-profile-arrow ${isProfileOpen ? 'open' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isProfileOpen && (
        <div className="user-profile-dropdown">
          <div className="user-profile-dropdown-header">
            <p className="user-profile-name">
              {user.fullName}
            </p>
            <p className="user-profile-email">
              {user.primaryEmailAddress?.emailAddress}
            </p>
            {false && isSupporter && (
              <div style={{ marginTop: '10px' }}>
                <span
                  className="supporter-badge"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    padding: '4px 8px',
                    borderRadius: 9999,
                    background: 'linear-gradient(135deg,#fbbf24 0%, #f59e0b 100%)',
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    boxShadow: '0 4px 12px rgba(245,158,11,0.2)'
                  }}
                >
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.2)'
                  }}>
                    <Star size={12} />
                  </span>
                  <span>{(supporterLevel || 'Supporter').toUpperCase()}</span>
                </span>
                {getSupporterDuration() && (
                  <p className="user-profile-supporter-duration" style={{ marginTop: 6 }}>
                    {t('auth.supportingFor', { duration: getSupporterDuration() })}
                  </p>
                )}
              </div>
            )}
          </div>
          
          {!isSupporter && (
            <button
              className="user-profile-dropdown-item supporter-upgrade"
            >
              <Star size={16} className="inline mr-1" /> {t('upgrade.becomeSupporter')}
            </button>
          )}
          <Link
            to="/settings"
            className="user-profile-dropdown-item"
            onClick={() => setIsProfileOpen(false)}
          >
            {t('sidebar.apiAccess')}
          </Link>
          <button
            onClick={handleSignOut}
            className="user-profile-dropdown-item"
          >
            {t('auth.signOut')}
          </button>
        </div>
      )}
    </div>
  );
};

export default UserProfile;